import React from 'react';
import _ from 'lodash';
import {
  Card, Label, CardBody, Form, InputGroup,
} from 'reactstrap';
import Button from 'app/components/general/Button';
import InputGroupAddon from 'app/components/general/InputGroupAddon';
import InputGroupBlock from 'app/components/general/InputGroupBlock';
import Phone from 'react-phone-number-input/input';
import classnames from 'classnames';
import { friendlyErrorMessage } from 'app/utils/parsers';
import Alert from 'app/components/general/Alert';
import { Link } from 'react-router-dom';
import PersonIcon from 'app/images/icons/person.svg';
import EmailIcon from 'app/images/icons/email.svg';
import PasswordIcon from 'app/images/icons/password.svg';
import MobileIcon from 'app/images/icons/mobile.svg';
import IActions from 'app/types/actions';
import StepNumberDivider from 'app/components/general/StepNumberDivider';
import validateInput from './PersonalDetailsValidator';

interface IPersonalDetailsProps {
  actions: IActions;
  isLoading: boolean;
  hasSaved: boolean;
  hasFailed: boolean;
  email?: string;
  fullName?: string;
  mobile?: string;
  backendErrors: any;
}

interface IPersonalDetailsState {
  email?: string;
  fullName?: string;
  mobile?: string;
  password?: string;
  backendErrors?: any;
  errors?: any;
  changed: boolean;
}

export default class PersonalDetails extends React.Component<
  IPersonalDetailsProps,
  IPersonalDetailsState
> {
  static defaultProps: IPersonalDetailsProps = {
    isLoading: false,
    hasSaved: false,
    hasFailed: false,
    actions: undefined,
    backendErrors: undefined,
  };

  readonly state: IPersonalDetailsState = {
    email: this.props.email || '',
    fullName: this.props.fullName || '',
    mobile: this.props.mobile || '',
    password: '',
    backendErrors: this.props.backendErrors || {},
    errors: {},
    changed: false,
  };

  static getDerivedStateFromProps(nextProps, prevState): any {
    if (!prevState.changed && nextProps.backendErrors !== prevState.backendErrors) {
      return { ...prevState, backendErrors: nextProps.backendErrors };
    }
    return null;
  }

  handleMobileChange = (e): void => {
    this.setState({ mobile: e, errors: {}, changed: true });
  };

  handleChange = (e): void => {
    this.setState({ backendErrors: {} });

    const state = {
      [e.target.name]: e.target.value,
      errors: {},
      changed: true,
    };
    this.setState(state);
  };

  handleSubmit = (e): void => {
    e.preventDefault();

    if (this.isValid()) {
      this.setState({ changed: false }, () => {
        this.props.actions.createNewUser({
          email: this.state.email,
          fullName: this.state.fullName,
          mobile: this.state.mobile,
          password: this.state.password,
        });
      });
    }
  }

  isValid(): boolean {
    const { errors, isValid } = validateInput(this.state);

    if (!isValid) {
      this.setState({ errors });
    }

    return isValid;
  }

  renderBackendErrors(): React.ReactNode {
    if (
      !_.isEmpty(this.state.backendErrors?.message)
    ) {
      return (
        <Alert color="error">
          {friendlyErrorMessage(this.state.backendErrors.message)}
        </Alert>
      );
    }
    return null;
  }

  render(): React.ReactNode {
    const { errors } = this.state;

    return (
      <div>
        <div className="row">
          <div className="col">
            <Card className="extra-shadow">
              <CardBody className="with-extra-padding">

                <div className="row">
                  <div className="col">
                    <h2>
                      Great, thanks!
                      <br />
                      Now, tell us about yourself
                    </h2>
                  </div>
                </div>
                <div className="row">
                  <div className="col with-padding">
                    <StepNumberDivider number={2} total={4} />
                    <Form onSubmit={this.handleSubmit}>
                      <div className="row">
                        <div className="col">
                          <InputGroupBlock
                            inputGroupClassName={classnames({
                              'is-invalid': errors.email,
                            }, 'large')}
                            type="email"
                            name="email"
                            icon={EmailIcon}
                            labelText="Email"
                            placeholder="Email"
                            aria-label="Email"
                            autoFocus
                            autoComplete="off"
                            errors={errors}
                            tabIndex="1"
                            value={this.state.email}
                            onChange={this.handleChange}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <InputGroupBlock
                            inputGroupClassName={classnames({
                              error: errors.fullName,
                            }, 'large')}
                            type="text"
                            name="fullName"
                            icon={PersonIcon}
                            labelText="Full name"
                            placeholder="Full name"
                            aria-label="Full name"
                            autoComplete="off"
                            errors={errors}
                            tabIndex="2"
                            value={this.state.fullName}
                            onChange={this.handleChange}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Label className="form-control-label" for="mobile">
                            Mobile
                          </Label>
                          <InputGroup className={classnames('large', { 'is-invalid': errors.mobile })}>
                            <InputGroupAddon icon={MobileIcon} />
                            <Phone
                              name="mobile"
                              country="AU"
                              placeholder="Your mobile number"
                              aria-label="Your mobile number"
                              autoComplete="off"
                              tabIndex="3"
                              className="form-control taller"
                              value={this.state.mobile}
                              onChange={this.handleMobileChange}
                            />
                          </InputGroup>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <InputGroupBlock
                            inputGroupClassName={classnames(
                              { error: errors.password },
                              'less-bottom-margin',
                              'large',
                            )}
                            type="password"
                            name="password"
                            icon={PasswordIcon}
                            labelText="Password"
                            placeholder="Password"
                            aria-label="Password"
                            autoComplete="new-password"
                            errors={errors}
                            tabIndex="4"
                            value={this.state.password}
                            onChange={this.handleChange}
                          />
                          <Label className="text-caption text-grey">
                            Password must be at least 8 characters long, and contain
                            letters and numbers.
                          </Label>
                        </div>
                      </div>
                      <div className="row with-margin-top-10">
                        <div className="col">
                          <Button
                            className="btn-1 btn-full-width success-sm large"
                            color="primary"
                            tabIndex="5"
                            loadingText="Creating"
                            isLoading={this.props.isLoading}
                            hasSaved={this.props.hasSaved}
                            hasFailed={!_.isEmpty(this.state.errors) || this.props.hasFailed}
                            disabled={
                              !this.state.email
                              || !this.state.fullName
                              || !this.state.password
                              || !this.state.mobile
                            }
                            type="submit"
                          >
                            Create account
                          </Button>
                        </div>
                      </div>
                    </Form>
                  </div>
                </div>
                {this.renderBackendErrors()}
              </CardBody>
            </Card>
          </div>
        </div>
        <div className="row justify-content-center">
          <div className="col-sm-8">
            <div className="signup-agreement text-grey">
              By creating an account you agree to the&nbsp;
              <a
                className="text-grey underline text-dark-grey-hover"
                href="https://www.teammo.com.au/policies/terms-conditions"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms &amp; Conditions
              </a>
              &nbsp;and&nbsp;
              <a
                className="text-grey underline text-dark-grey-hover"
                href="https://www.teammo.com.au/policies/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
            </div>

            <div className="alternate-options text-grey">
              Already have an account?&nbsp;
              <Link to="/login">Log in</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
